import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import CoolDashboardNavbar from './CoolDashboardNavbar';
import CoolDashboardSidebar from './CoolDashboardSidebar';

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 72;

const RootStyle = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden',
});

const MainStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100vh',
  paddingTop: APP_BAR_MOBILE + 24,
  paddingBottom: theme.spacing(10),
  background: 'linear-gradient(180deg, #f0f2ff 0%, #e8eaf6 50%, #f5f0ff 100%)',
  [theme.breakpoints.up('lg')]: {
    paddingTop: APP_BAR_DESKTOP + 24,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}));

// Global premium CSS overrides for all child pages
const premiumOverrides = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  .cool-dashboard-main, .cool-dashboard-main * {
    font-family: 'Inter', -apple-system, sans-serif !important;
  }

  /* ===== CARDS ===== */
  .cool-dashboard-main .MuiCard-root {
    border-radius: 16px !important;
    border: 1px solid rgba(99, 102, 241, 0.08);
    box-shadow: 0 4px 24px rgba(0,0,0,0.04) !important;
    transition: transform 0.25s ease, box-shadow 0.25s ease !important;
  }
  .cool-dashboard-main .MuiCard-root:hover {
    box-shadow: 0 8px 32px rgba(99, 102, 241, 0.1) !important;
  }

  /* ===== FORM INPUTS ===== */
  .cool-dashboard-main .MuiOutlinedInput-root {
    border-radius: 10px !important;
    background: rgba(255,255,255,0.7);
    transition: all 0.2s ease;
  }
  .cool-dashboard-main .MuiOutlinedInput-root:hover {
    background: #fff;
  }
  .cool-dashboard-main .MuiOutlinedInput-root.Mui-focused {
    background: #fff;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
  }
  .cool-dashboard-main .MuiOutlinedInput-notchedOutline {
    border-color: rgba(99, 102, 241, 0.15) !important;
  }
  .cool-dashboard-main .Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #6366f1 !important;
  }
  .cool-dashboard-main .MuiInputLabel-root.Mui-focused {
    color: #6366f1 !important;
  }

  /* ===== BUTTONS ===== */
  .cool-dashboard-main .MuiButton-contained {
    border-radius: 10px !important;
    text-transform: none !important;
    font-weight: 600 !important;
    transition: all 0.2s ease !important;
  }
  .cool-dashboard-main .MuiButton-contained:hover {
    transform: translateY(-1px);
  }

  /* ===== TABLES ===== */
  .cool-dashboard-main .MuiTableHead-root {
    background: linear-gradient(135deg, #1e1b4b 0%, #312e81 100%) !important;
  }
  .cool-dashboard-main .MuiTableHead-root .MuiTableCell-head {
    color: #e0e7ff !important;
    font-weight: 700 !important;
    font-size: 0.78rem !important;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    border-bottom: none !important;
    padding-top: 14px !important;
    padding-bottom: 14px !important;
  }
  .cool-dashboard-main .MuiTableHead-root .MuiTableSortLabel-root {
    color: #e0e7ff !important;
  }
  .cool-dashboard-main .MuiTableHead-root .MuiTableSortLabel-root:hover {
    color: #fff !important;
  }
  .cool-dashboard-main .MuiTableBody-root .MuiTableRow-root {
    transition: background 0.15s ease;
  }
  .cool-dashboard-main .MuiTableBody-root .MuiTableRow-root:hover {
    background: rgba(99, 102, 241, 0.04) !important;
  }
  .cool-dashboard-main .MuiTableBody-root .MuiTableCell-root {
    border-bottom: 1px solid rgba(99, 102, 241, 0.06) !important;
  }

  /* ===== CHIPS ===== */
  .cool-dashboard-main .MuiChip-root {
    border-radius: 8px !important;
    font-weight: 600 !important;
  }
  .cool-dashboard-main .MuiChip-colorSuccess {
    background: linear-gradient(135deg, #10b981, #059669) !important;
    color: #fff !important;
  }
  .cool-dashboard-main .MuiChip-colorError {
    background: linear-gradient(135deg, #ef4444, #dc2626) !important;
    color: #fff !important;
  }

  /* ===== DIALOGS ===== */
  .cool-dashboard-main .MuiDialog-paper {
    border-radius: 16px !important;
  }

  /* ===== TABS ===== */
  .cool-dashboard-main .MuiTab-root.Mui-selected {
    color: #6366f1 !important;
    font-weight: 700;
  }
  .cool-dashboard-main .MuiTabs-indicator {
    background: linear-gradient(90deg, #6366f1, #8b5cf6) !important;
    height: 3px;
    border-radius: 3px;
  }

  /* ===== PAGINATION ===== */
  .cool-dashboard-main .MuiPaginationItem-root.Mui-selected {
    background: #6366f1 !important;
    color: #fff !important;
  }

  /* ===== AVATAR ===== */
  .cool-dashboard-main .MuiAvatar-root {
    border: 2px solid rgba(99, 102, 241, 0.15);
  }

  /* ===== ALERTS ===== */
  .cool-dashboard-main .MuiAlert-root {
    border-radius: 12px !important;
  }

  /* ===== SCROLLBAR ===== */
  .cool-dashboard-main ::-webkit-scrollbar {
    width: 5px; height: 5px;
  }
  .cool-dashboard-main ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    border-radius: 4px;
  }
  .cool-dashboard-main ::-webkit-scrollbar-track {
    background: rgba(99, 102, 241, 0.04);
  }

  /* ===== TYPOGRAPHY ===== */
  .cool-dashboard-main h4.MuiTypography-root,
  .cool-dashboard-main h3.MuiTypography-root {
    font-weight: 800 !important;
    letter-spacing: -0.3px;
  }

  /* ===== FADE IN ===== */
  @keyframes coolFadeIn {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .cool-dashboard-main > * {
    animation: coolFadeIn 0.4s ease-out;
  }
`;

export default function CoolDashboardLayout() {
  const [open, setOpen] = useState(false);

  return (
    <RootStyle>
      <style>{premiumOverrides}</style>
      <CoolDashboardNavbar onOpenSidebar={() => setOpen(true)} />
      <CoolDashboardSidebar isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />
      <MainStyle className="cool-dashboard-main">
        <Outlet />
      </MainStyle>
    </RootStyle>
  );
}
